/**
 * ISSUE-027: signed-URL expiry conventions differ per storage/CDN layer.
 * - S3 presigned (SigV4): `X-Amz-Expires` is a duration in seconds anchored
 *   at `X-Amz-Date` (ISO 8601 basic format, e.g. `20260821T120000Z`)
 * - CloudFront signed: `Expires` is an absolute Unix epoch in seconds
 * Both are normalised to an absolute expiry timestamp in ms. `null` means
 * expiry-unknown — callers must re-fetch the resource before using the URL
 * rather than assuming it is still valid.
 */
const AMZ_DATE_PATTERN = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;

const parseAmzDate = (value: string | null) => {
  const match = value?.match(AMZ_DATE_PATTERN);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  return Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);
};

export const getUrlExpiry = (url: string): number | null => {
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return null;
  }
  const amzExpires = params.get('X-Amz-Expires');
  if (amzExpires !== null) {
    const durationSeconds = parseInt(amzExpires, 10);
    if (!Number.isNaN(durationSeconds)) {
      // A freshly issued URL has X-Amz-Date ≈ now, so falling back to
      // Date.now() preserves the legacy derivation when the date is absent.
      const signedAt = parseAmzDate(params.get('X-Amz-Date'));
      return (signedAt ?? Date.now()) + durationSeconds * 1000;
    }
  }
  const expires = params.get('Expires');
  if (expires !== null) {
    const epochSeconds = parseInt(expires, 10);
    if (!Number.isNaN(epochSeconds)) {
      return epochSeconds * 1000;
    }
  }
  return null;
};
